import { apiFetch } from './client.js';

/** Карточка счёта 41: /api/admin/card41/*. Доступ — admin или CARD41_USERS (сервер отвечает 403 остальным). */

export const BALANCE_COLUMNS = [
  'ts_in', 'ts_out', 'ts_writeoff', 'imp', 'buy_rf', 'resort', 'kit', 'repack', 'corr_in', 'other_in',
  'sales', 'otbrak', 'loss94', 'other_out', 'stock_ts', 'stock',
] as const;
export type BalanceKey = (typeof BALANCE_COLUMNS)[number];
export const BALANCE_LABELS: Record<BalanceKey, string> = {
  ts_in: 'Вход на ТС', ts_out: 'Выход с ТС', ts_writeoff: 'Списано с ТС', imp: 'Импорт', buy_rf: 'Закупка в РФ',
  resort: 'Пересортица', kit: 'Комплектация наборов', repack: 'Переупаковка', corr_in: 'Корректировки (+)', other_in: 'Прочие поступления',
  sales: 'Продажи', otbrak: 'Отбраковка', loss94: 'Потери (94)', other_out: 'Прочие списания',
  stock_ts: 'Остаток на ТС', stock: 'Остаток',
};
export type SkuGroup = 'стики' | 'эсдн' | 'устройство' | 'набор' | 'прочее';

export type Card41File = {
  id: string; uploadedAt: string; fileName: string; format: 'quantity' | 'bu_quantity' | 'flat' | null;
  periodFrom: string | null; periodTo: string | null; firstMove: string | null; lastMove: string | null;
  movesCount: number; skuCount: number; openingBalance: string; status: 'parsed' | 'failed'; error: string | null; isCurrent: boolean;
};
export type SkuBalance = Record<BalanceKey, number> & { sku: string; group: SkuGroup; check: number };
export type StatementRow = { section: string; op: string; where: string; qty: number; docs: number; from: string; to: string; net: number };
export type Statement = { rows: StatementRow[]; stock: { acc: string; wh: string | null; qty: number }[]; calc: number; fact: number; resortNet: number };
export type GroupTotals = { group: SkuGroup; skus: number; totals: Record<BalanceKey, number>; checkFailures: number };
export type Card41Summary = {
  file: Card41File; totals: Record<BalanceKey, number>; groups: GroupTotals[]; statement: Statement; checkFailures: number; unclassified: number;
};

export type BalanceRow = { key: string; label: string; sign: -1 | 0 | 1; item: number; sets: number; total: number; box: number | null; source: string };
export type Pair3 = { item: number; sets: number; total: number };
export type GroupBalance = {
  group: 'стики' | 'эсдн' | 'устройство' | 'набор'; label: string; unit: string; perSet: number; box: number | null;
  rows: BalanceRow[]; expected: Pair3; factRows: BalanceRow[]; fact: Pair3; diff: Pair3; reference: BalanceRow[]; formula: string; note: string;
};
export type GroupsResponse = { balances: GroupBalance[]; salesFetchedAt: string | null; salesSource: string | null; error: string | null };

/* «Сверка» → «Итоги периода»: зеркало apps/api/src/services/period/{summary-types,summary-tables,events}.ts */
export type PeriodGroup = 'стики' | 'набор' | 'устройство' | 'эсдн';
export type Period = { kind: 'week' | 'month'; from: string; to: string; prevFrom: string; prevTo: string; dataUntil: string; running: boolean };
export type Bucket = { tke: number; npi: number; ts: number };
export type EventItem = { name: string; group: PeriodGroup; qty: number };
export type PeriodEvent = {
  day: string; company: 'tke' | 'npi';
  type: 'shipments' | 'returns' | 'price_correction' | 'move' | 'ts_release' | 'sale_between' | 'card_doc' | 'card_realization' | 'unexplained_out' | 'unexplained_in';
  title: string; place: string | null; items: EventItem[]; sum: number | null;
  clients?: { name: string; items: EventItem[]; sum: number }[];
  source: 'продажи 1С' | 'ведомость' | 'карточка 41'; docs: string[]; note: string | null;
};
/** Отгрузки (строки 1С с плюсом); ret — возвраты от покупателей по модулю (Р2: из отгрузок не вычитаются). */
export type ShipSku = { name: string; tke: number; npi: number; sum: number; prevQty: number; ret: number };
export type PeriodSummary = {
  period: Period;
  shipments: { groups: (ShipSku & { group: PeriodGroup; skus: ShipSku[] })[]; total: { sum: number; prevSum: number; clients: number; shipments: number; retQty: number; retSum: number; corrSum: number } };
  stock: { startDate: string; endDates: { tke: string | null; npi: string | null };
    missing: { start: { tke: boolean; npi: boolean }; end: { tke: boolean; npi: boolean } }; groups: { group: PeriodGroup; end: Bucket; start: Bucket; skus: { name: string; end: Bucket; start: Bucket }[] }[] };
  events: { days: { day: string; events: PeriodEvent[] }[]; notes: { cardUntil: string | null; cardUploaded: string | null; missingLetters: string[]; dataUntil: string } };
};

export const card41Api = {
  periods: () => apiFetch<{ periods: Period[] }>('/api/admin/card41/periods'),
  period: (kind: 'week' | 'month', from: string) => apiFetch<PeriodSummary>(`/api/admin/card41/period?kind=${kind}&from=${from}`),
  groups: (id: string) => apiFetch<GroupsResponse>(`/api/admin/card41/${id}/groups`),
  refreshSales: (id: string) => apiFetch<{ balances: GroupBalance[]; error: string | null }>(`/api/admin/card41/${id}/refresh-sales`, { method: 'POST', body: '{}' }),
  access: () => apiFetch<{ enabled: boolean }>('/api/admin/card41/access'),
  list: () => apiFetch<{ files: Card41File[] }>('/api/admin/card41'),
  summary: (id: string) => apiFetch<Card41Summary>(`/api/admin/card41/${id}`),
  sku: (id: string) => apiFetch<{ balances: SkuBalance[] }>(`/api/admin/card41/${id}/sku`),
  xlsxUrl: (id: string) => `/api/admin/card41/${id}/xlsx`,
  async upload(file: File): Promise<{ fileId: string; summary: Card41Summary }> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const res = await fetch('/api/admin/card41/upload', { method: 'POST', body: fd, credentials: 'include' });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      throw Object.assign(new Error(String(body['error'] ?? `http_${res.status}`)), { status: res.status, body });
    }
    return body as { fileId: string; summary: Card41Summary };
  },
};
